import React, { useState, useEffect } from "react";
import { BarChart, Bar, PieChart, Pie, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis, CartesianGrid, Legend } from "recharts";
import { api } from "../../utils/api";

const COLORS = ["#ef4444", "#f97316", "#f59e0b", "#84cc16", "#10b981", "#06b6d4", "#3b82f6", "#8b5cf6", "#d946ef", "#f43f5e"];
const monthNames = ["Oca", "Şub", "Mar", "Nis", "May", "Haz", "Tem", "Ağu", "Eyl", "Eki", "Kas", "Ara"];

export default function ExpenseReport() {
  const [loading, setLoading] = useState(true);
  const [monthlyData, setMonthlyData] = useState([]);
  const [categoryData, setCategoryData] = useState([]);
  const [periodInfo, setPeriodInfo] = useState(null);
  const [totalExpense, setTotalExpense] = useState(0);

  useEffect(() => {
    // Hem Dashboard verisini (aktif dönemi bulmak için) hem de Giderleri aynı anda çekiyoruz
    Promise.all([
      api.get("/dashboard"),
      api.get("/transactions?kind=expense")
    ]).then(([dashRes, txsRes]) => {
      const ap = dashRes.data.active_period;
      const txs = txsRes.data;

      if (!ap || !ap.start_date || !ap.end_date) {
        setLoading(false);
        return; // Aktif dönem yoksa uyarı göstereceğiz
      }

      const sDate = new Date(ap.start_date);
      const eDate = new Date(ap.end_date);
      
      setPeriodInfo({
        name: ap.name,
        start: sDate,
        end: eDate
      });

      const today = new Date();
      // Gelecek ayları grafikte boş göstermemek için: Dönem bitmediyse bugüne kadar, bittiyse bitiş tarihine kadar çiz.
      const chartEndDate = eDate > today ? today : eDate;

      // 1. Grafiğin X ekseni için ayları dönem başlangıcından itibaren hazırla (Sıfır çeken aylar da görünsün diye)
      const monthlyObj = {};
      let currentMonth = new Date(sDate.getFullYear(), sDate.getMonth(), 1);
      const endLimit = new Date(chartEndDate.getFullYear(), chartEndDate.getMonth(), 1);

      while (currentMonth <= endLimit) {
        const monthKey = `${currentMonth.getFullYear()}-${String(currentMonth.getMonth() + 1).padStart(2, '0')}`;
        const monthLabel = `${monthNames[currentMonth.getMonth()]}'${currentMonth.getFullYear().toString().slice(2)}`; // Örn: May'26
        monthlyObj[monthKey] = { key: monthKey, name: monthLabel, tutar: 0 };
        
        currentMonth.setMonth(currentMonth.getMonth() + 1); // Bir sonraki aya geç
      }

      const catObj = {};
      let total = 0;

      // 2. İşlemleri döneme göre filtrele ve topla
      txs.forEach(tx => {
        if (!tx.date) return;
        const txDate = new Date(tx.date);

        // SADECE AKTİF DÖNEM TARİHLERİ ARASINDAKİ GİDERLERİ DAHİL ET
        if (txDate >= sDate && txDate <= eDate) {
          const amt = Number(tx.amount) || 0;
          total += amt;

          // Aylık toplama ekle
          const monthKey = `${txDate.getFullYear()}-${String(txDate.getMonth() + 1).padStart(2, '0')}`;
          if (monthlyObj[monthKey]) {
            monthlyObj[monthKey].tutar += amt;
          } else {
            // Eğer sistemde istisnai bir ay varsa onu da yakala
            const mLabel = `${monthNames[txDate.getMonth()]}'${txDate.getFullYear().toString().slice(2)}`;
            monthlyObj[monthKey] = { key: monthKey, name: mLabel, tutar: amt };
          }

          // Kalem / Kategori bazlı toplama ekle
          const cat = tx.category || "Diğer";
          if (!catObj[cat]) {
            catObj[cat] = { name: cat, value: 0 };
          }
          catObj[cat].value += amt;
        }
      });

      // Grafiğin anlayacağı formata çevir ve sırala
      const mData = Object.values(monthlyObj).sort((a, b) => a.key.localeCompare(b.key));
      const cData = Object.values(catObj).sort((a, b) => b.value - a.value); // En çok harcanan kalem en üstte

      setMonthlyData(mData);
      setCategoryData(cData);
      setTotalExpense(total);
      setLoading(false);
    }).catch(err => {
      console.error("Gider raporu verileri çekilemedi:", err);
      setLoading(false);
    });
  }, []);

  const formatTL = (value) => new Intl.NumberFormat("tr-TR", { style: "currency", currency: "TRY", maximumFractionDigits: 0 }).format(value);

  if (loading) return <div style={{ padding: "40px", textAlign: "center", color: "#64748b" }}>Gider verileri derleniyor...</div>;

  if (!periodInfo) return (
    <div style={{ padding: "40px", textAlign: "center", color: "#ef4444", background: "white", borderRadius: "16px", border: "1px solid #fee2e2" }}>
      <h3>Aktif Dönem Bulunamadı!</h3>
      <p>Giderleri raporlayabilmek için Dönemler menüsünden bir dönemi aktif hale getirmelisiniz.</p>
    </div>
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "24px", animation: "fadeIn 0.4s ease-out" }}>
      
      {/* ÜST PANO: Aktif Dönem ve Toplam Gider Bilgisi */}
      <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", alignItems: "center", background: "white", padding: "24px 30px", borderRadius: "16px", border: "1px solid #e2e8f0", boxShadow: "0 4px 6px rgba(0,0,0,0.02)", gap: "20px" }}>
        <div>
          <span style={{ fontSize: "12px", fontWeight: "bold", color: "#3b82f6", letterSpacing: "1px", textTransform: "uppercase" }}>AKTİF DÖNEM GİDER RAPORU</span>
          <h2 style={{ margin: "6px 0", fontSize: "22px", color: "#1e293b" }}>{periodInfo.name} Dönemi</h2>
          <p style={{ margin: 0, fontSize: "14px", color: "#64748b", display: "flex", alignItems: "center", gap: "6px" }}>
            <span style={{width: "8px", height: "8px", borderRadius: "50%", background: "#10b981", display: "inline-block"}}></span>
            {periodInfo.start.toLocaleDateString("tr-TR")} — {periodInfo.end.toLocaleDateString("tr-TR")}
          </p>
        </div>
        <div style={{ textAlign: "right", borderLeft: "2px solid #f1f5f9", paddingLeft: "30px" }}>
          <span style={{ fontSize: "13px", color: "#64748b", textTransform: "uppercase", fontWeight: "600" }}>Toplam Dönem Gideri</span>
          <div style={{ fontSize: "32px", fontWeight: "bold", color: "#ef4444", marginTop: "4px" }}>
            {formatTL(totalExpense)}
          </div>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: "24px" }}>
        
        {/* SOL KART: Aylara Göre Giderler (Çubuk Grafik) */}
        <div style={{ background: "white", padding: "24px", borderRadius: "16px", border: "1px solid #e2e8f0", boxShadow: "0 4px 6px rgba(0,0,0,0.02)" }}>
          <h3 style={{ marginTop: 0, marginBottom: "20px", fontSize: "16px", color: "#1e293b" }}>Aylara Göre Gider Eğilimi</h3>
          <div style={{ height: "300px", width: "100%" }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={monthlyData}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                <XAxis dataKey="name" tick={{ fontSize: 12, fill: "#64748b" }} axisLine={false} tickLine={false} dy={10} />
                <YAxis tickFormatter={(val) => `₺${val}`} tick={{ fontSize: 12, fill: "#64748b" }} axisLine={false} tickLine={false} width={70} />
                <Tooltip formatter={(value) => [formatTL(value), "Toplam Gider"]} cursor={{fill: "#f8fafc"}} contentStyle={{ borderRadius: "8px", border: "none", boxShadow: "0 4px 12px rgba(0,0,0,0.1)" }} />
                <Bar dataKey="tutar" fill="#ef4444" radius={[6, 6, 0, 0]} barSize={40} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* SAĞ KART: Gider Kalemlerine Göre (Pasta Grafik) */}
        <div style={{ background: "white", padding: "24px", borderRadius: "16px", border: "1px solid #e2e8f0", boxShadow: "0 4px 6px rgba(0,0,0,0.02)" }}>
          <h3 style={{ marginTop: 0, marginBottom: "20px", fontSize: "16px", color: "#1e293b" }}>Gider Kalemi Dağılımı</h3>
          <div style={{ height: "300px", width: "100%" }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={categoryData} cx="50%" cy="50%" innerRadius={75} outerRadius={105} paddingAngle={3} dataKey="value" nameKey="name" stroke="none">
                  {categoryData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => [formatTL(value), "Tutar"]} contentStyle={{ borderRadius: "8px", border: "none", boxShadow: "0 4px 12px rgba(0,0,0,0.1)" }} />
                <Legend layout="vertical" verticalAlign="middle" align="right" wrapperStyle={{ fontSize: "13px", color: "#475569" }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

      </div>

      {/* ALT KART: Detaylı Kalem Listesi Tablosu */}
      <div style={{ background: "white", padding: "24px", borderRadius: "16px", border: "1px solid #e2e8f0", boxShadow: "0 4px 6px rgba(0,0,0,0.02)" }}>
        <h3 style={{ marginTop: 0, marginBottom: "20px", fontSize: "16px", color: "#1e293b" }}>Kalem Bazlı Gider Özeti</h3>
        <table style={{ width: "100%", borderCollapse: "collapse", textAlign: "left" }}>
          <thead>
            <tr style={{ borderBottom: "2px solid #e2e8f0", color: "#64748b", fontSize: "13px" }}>
              <th style={{ padding: "12px 8px" }}>Gider Kalemi (Kategori)</th>
              <th style={{ padding: "12px 8px", textAlign: "right" }}>Dönem Toplamı</th>
            </tr>
          </thead>
          <tbody>
            {categoryData.length === 0 ? (
              <tr><td colSpan="2" style={{ padding: "20px", textAlign: "center", color: "#94a3b8" }}>Bu dönemde henüz kaydedilmiş bir gider bulunmuyor.</td></tr>
            ) : categoryData.map((cat, i) => (
              <tr key={i} style={{ borderBottom: "1px solid #f8fafc" }}>
                <td style={{ padding: "14px 8px", display: "flex", alignItems: "center", gap: "10px", fontWeight: "500", color: "#334155" }}>
                  <span style={{ width: "12px", height: "12px", borderRadius: "50%", backgroundColor: COLORS[i % COLORS.length] }}></span>
                  {cat.name}
                </td>
                <td style={{ padding: "14px 8px", textAlign: "right", fontWeight: "bold", color: "#1e293b" }}>
                  {formatTL(cat.value)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

    </div>
  );
}